import React, { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, CheckCircle2, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import ParticleField from '../components/ParticleField';
import FloatingLogo from '../components/FloatingLogo';
import { Reveal } from '../components/Footer';
import { brand, serviceOptions, industries, regions, orgTypes, orgSizes, hearAbout } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  firstName: '', lastName: '', email: '', organisation: '', role: '', phone: '',
  industry: '', region: '', orgType: '', orgSize: '', challenge: '', techStack: '', goal: '',
  service: '', hear: '', consent: false,
};

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.organisation || !form.challenge) {
      toast({ title: 'Please complete the required fields', description: 'First name, email, organisation and current challenge are required.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSubmitted(true);
      toast({ title: 'Enquiry received', description: 'Thank you. We\u2019ll be in touch to arrange a discovery conversation.' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'We could not submit your enquiry. Please try again, or email us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <section className="hero-dark relative overflow-hidden">
        <ParticleField density={0.9} />
        <div className="absolute inset-0 hidden md:block pointer-events-none">
          <FloatingLogo src={brand.logoTransparent} size={190} speed={0.5} vary />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 pt-40 pb-20 md:pt-44 md:pb-24">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#c6a94b]">Contact</p>
            <h1 className="mt-5 font-serif text-[36px] md:text-[56px] leading-[1.08] text-white">
              Discuss your AI requirements
            </h1>
            <p className="mt-6 mx-auto max-w-2xl text-[17px] leading-relaxed text-[#c4d0cd]">
              Share a little about your business and challenges.
              <br />
              Deeper qualification happens once we connect.
            </p>
          </Reveal>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-[#faf9f5]" />
      </section>

      <section className="bg-[#faf9f5] py-24">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 grid gap-12 lg:grid-cols-12 lg:items-start">
          {/* Info column */}
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-serif text-[30px] text-[#22312f]">What happens next</h2>
              <ol className="mt-6 space-y-5">
                {['We review your enquiry and confirm fit.', 'We arrange a focused discovery conversation.', 'Together we shape a governed, practical next step.'].map((t, i) => (
                  <li key={t} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2b6d7c] to-[#c6a94b] text-[13px] font-semibold text-white">{i + 1}</span>
                    <span className="text-[15px] leading-relaxed text-[#5a6668]">{t}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-8 space-y-3 border-t border-[#e7e5dd] pt-6 text-[14px] text-[#5a6668]">
                <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#1f5866]" /> hello@herbertprojectpartners.com</p>
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#1f5866]" /> {brand.legal.location}</p>
              </div>
              <div className="mt-12 rounded-xl border border-[#e7e5dd] bg-white p-5">
                <p className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#22312f]"><ShieldCheck className="h-4 w-4 text-[#c6a94b]" /> Handled responsibly</p>
                <p className="mt-2 text-[13px] leading-relaxed text-[#5a6668]">We collect only what is needed for a defined business purpose and restrict access appropriately.</p>
              </div>
            </Reveal>
          </div>

          {/* Form column */}
          <div className="lg:col-span-8">
            <Reveal delay={100}>
              {submitted ? (
                <div className="rounded-3xl border border-[#eceae2] bg-white p-10 md:p-14 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#eef3f1]">
                    <CheckCircle2 className="h-8 w-8 text-[#1f5866]" />
                  </div>
                  <h2 className="mt-6 font-serif text-[32px] text-[#22312f]">Thank you, {form.firstName}</h2>
                  <p className="mt-4 text-[16px] leading-relaxed text-[#5a6668] max-w-md mx-auto">
                    Your enquiry has been received. We’ll be in touch to arrange a discovery conversation about your AI requirements.
                  </p>
                  <Button onClick={() => { setForm(initial); setSubmitted(false); }} variant="outline" className="mt-8 rounded-full h-11 px-6 border-[#1f5866] text-[#1f5866] hover:bg-[#eef3f1]">
                    Submit another enquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="rounded-3xl border border-[#eceae2] bg-white p-7 md:p-10">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="First name *"><Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="Jane" /></Field>
                    <Field label="Last name"><Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Doe" /></Field>
                    <Field label="Work email *"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@company.com" /></Field>
                    <Field label="Phone (optional)"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+61 ..." /></Field>
                    <Field label="Organisation *"><Input value={form.organisation} onChange={(e) => set('organisation', e.target.value)} placeholder="Company / organisation" /></Field>
                    <Field label="Role / title"><Input value={form.role} onChange={(e) => set('role', e.target.value)} placeholder="e.g. Head of Strategy" /></Field>

                    <Field label="Industry">
                      <SelectField value={form.industry} onChange={(v) => set('industry', v)} placeholder="Select industry" options={industries} />
                    </Field>
                    <Field label="Region">
                      <SelectField value={form.region} onChange={(v) => set('region', v)} placeholder="Select region" options={regions} />
                    </Field>
                    <Field label="Organisation type">
                      <SelectField value={form.orgType} onChange={(v) => set('orgType', v)} placeholder="Select type" options={orgTypes} />
                    </Field>
                    <Field label="Organisation size">
                      <SelectField value={form.orgSize} onChange={(v) => set('orgSize', v)} placeholder="Select size" options={orgSizes} />
                    </Field>
                  </div>

                  <div className="mt-5 grid gap-5">
                    <Field label="Current challenge *">
                      <Textarea value={form.challenge} onChange={(e) => set('challenge', e.target.value)} placeholder="What are you trying to solve or achieve with AI?" rows={3} />
                    </Field>
                    <Field label="What is your current tech stack - if you have one">
                      <Textarea value={form.techStack} onChange={(e) => set('techStack', e.target.value)} placeholder="e.g. cloud, data, CRM, AI tools you already use" rows={2} />
                    </Field>
                    <Field label="Business goal">
                      <Textarea value={form.goal} onChange={(e) => set('goal', e.target.value)} placeholder="What does a good outcome look like?" rows={2} />
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Service of interest">
                        <SelectField value={form.service} onChange={(v) => set('service', v)} placeholder="Select a service" options={serviceOptions} />
                      </Field>
                      <Field label="How did you hear about us?">
                        <SelectField value={form.hear} onChange={(v) => set('hear', v)} placeholder="Select" options={hearAbout} />
                      </Field>
                    </div>
                  </div>

                  <label className="mt-6 flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-0.5" />
                    <span className="text-[13px] leading-relaxed text-[#5a6668]">
                      I consent to Herbert Project Partners using and storing the information I provide to respond to and manage my enquiry.
                    </span>
                  </label>

                  <Button type="submit" disabled={loading} className="btn-gold mt-8 w-full sm:w-auto rounded-full h-12 px-8 text-[14px] font-semibold disabled:opacity-70">
                    {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>) : 'Submit enquiry'}
                  </Button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-2 block text-[13px] font-medium text-[#3f4d4b]">{label}</Label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
